import { useState } from "react";
import { motion } from "framer-motion";
import { Youtube, Facebook, Radio, Play } from "lucide-react";

const SOURCES = {
  youtube: {
    label: "YouTube",
    icon: Youtube,
    src: "https://www.youtube.com/embed/NrCTOZQE8qg",
  },
  facebook: {
    label: "Facebook",
    icon: Facebook,
    src: "https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fapostle.lucas%2Fvideos%2F1729283034786983&show_text=false&width=560&t=0",
  },
};

const PAST = [
  { title: "Leadership as A Man", date: "Aug 25, 2026", tag: "Apostolic Summit", url: "https://www.youtube.com/watch?v=sSXhSpYOLh8&t=1090s" },
  { title: "The Apostolic Masterclass with Chief Apostle Leonard Lucas Jr.", date: "Jul 6, 2026", tag: "Masterclass", url: "https://www.youtube.com/watch?v=HPZbOx6MXVA&t=2048s" },
  { title: "Apostolic Masterclass", date: "May 25, 2026", tag: "Masterclass", url: "https://www.youtube.com/watch?v=aPdyEEOlCO8" },
];

export default function LiveStream() {
  const [source, setSource] = useState("youtube");
  const active = SOURCES[source];

  return (
    <div className="pt-28 md:pt-32 pb-24 bg-charcoal min-h-screen text-sand" data-testid="live-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
        >
          <div>
            <p className="overline text-terracotta inline-flex items-center gap-2">
              <Radio size={15} className="animate-pulse" /> Live &amp; On Demand
            </p>
            <h1 className="mt-3 font-heading text-5xl sm:text-6xl tracking-tight leading-none">
              The Live <span className="italic text-terracotta">Stream</span>
            </h1>
            <p className="mt-4 font-body text-sand/70 max-w-lg">
              Join from wherever you are. We go live every Sunday morning and for special
              gatherings throughout the week.
            </p>
          </div>

          <div className="flex gap-2 p-1.5 rounded-full bg-white/5 border border-white/10 w-fit">
            {Object.entries(SOURCES).map(([key, s]) => {
              const Icon = s.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSource(key)}
                  data-testid={`live-source-${key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
                    source === key
                      ? "bg-terracotta text-white"
                      : "text-sand/70 hover:text-sand"
                  }`}
                >
                  <Icon size={16} /> {s.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          key={source}
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black"
          style={{ aspectRatio: "16 / 9" }}
          data-testid="live-player"
        >
          <iframe
            title={`${active.label} stream`}
            src={active.src}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </motion.div>

        <div className="mt-16">
          <p className="overline text-terracotta">Recent Messages</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl">Catch up on past streams</h2>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {PAST.map((p, i) => (
              <motion.a
                key={i}
                href={p.url || undefined}
                target={p.url ? "_blank" : undefined}
                rel={p.url ? "noopener noreferrer" : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`past-stream-${i}`}
                className="group block rounded-2xl bg-white/5 border border-white/10 p-6 transition-all hover:bg-white/10 hover:-translate-y-1 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <span className="overline text-terracotta">{p.tag}</span>
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-terracotta/20 text-terracotta transition-colors group-hover:bg-terracotta group-hover:text-white">
                    <Play size={16} fill="currentColor" />
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-2xl text-sand leading-snug">{p.title}</h3>
                <p className="mt-2 font-body text-sm text-sand/50">{p.date}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
